import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { GeoPoint, I18nModule, PointOfService } from '@spartacus/core';
import { AddressCardComponent } from '../../../../misc/address-card/address-card.component';
import { CustomStoreFinderService } from '../../core/custom-store-finder.service';
import { AlertComponent } from '../../../../../../components/alert/alert.component';
import { LocalLoaderComponent } from '../../../../../../components/loader/local-loader/local-loader.component';
import { Alert, ALERT_TYPE } from '../../../../../../components/alert/alert.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'addresses-with-map',
  standalone: true,
  imports: [CommonModule, I18nModule, GoogleMapsModule, AddressCardComponent, AlertComponent, LocalLoaderComponent],
  templateUrl: './addresses-with-map.component.html',
  styleUrl: './addresses-with-map.component.scss',
})
export class AddressesWithMapComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleMap) mapElement!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow?: MapInfoWindow;
  @ViewChild('listContainer') listContainer!: ElementRef<HTMLUListElement>;

  infoMessage: Alert = {
    isActive: true,
    content: '',
    containerClass: 'rounded-0',
    hasCloseButton: false,
    type: ALERT_TYPE.INFO,
  };

  pointOfServicesRequest$ = this.customStoreFinderService.pointOfServicesRequest$;
  pointOfServicesRequestConfig$ = this.customStoreFinderService.pointOfServicesRequestConfig$;
  subscription = new Subscription();
  selectedPosCode?: string;
  selectedPOS?: PointOfService;
  center = { lat: 39.8283, lng: -98.5795 };
  zoom = 15;
  circleRadius = {
    fillColor: 'rgba(0, 150, 255, 0.5)',
    strokeColor: '#00aaff',
    strokeWeight: 2,
    clickable: false,
  };

  constructor(
    private customStoreFinderService: CustomStoreFinderService,
    private cdr: ChangeDetectorRef,
  ) {}

  alertMessage: Alert = {
    isActive: true,
    title: '',
    content: '',
    hasCloseButton: false,
    type: ALERT_TYPE.DANGER,
  };

  ngOnInit(): void {
    // this.center = this.getCenter();
    // setTimeout(() => this.fitBounds(), 500);

    this.listnerRequest();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  fomattedLabelType(label: string): string {
    return label
      .replace('_', ' ')
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  getPin(pos: PointOfService): string {
    const pinDistributorIcon = '../../../../../../../assets/libs/icons/pin-distributor.png';
    const pinSalesRepIcon = '../../../../../../../assets/libs/icons/pin-salesRep.png';

    return pos.type === 'DISTRIBUTOR' ? pinDistributorIcon : pinSalesRepIcon;
  }

  listnerRequest() {
    this.subscription.add(
      this.pointOfServicesRequest$.subscribe(response => {
        if (response.hasError) {
          this.alertMessage.content = response.message;
        } else {
          if (response.pointOfServices && response.pointOfServices.length > 0) {
            this.selectAddress(response.pointOfServices[0].name!);
            this.infoMessage.content = response.infoMessage;

            if (response.noPOS) {
              if (response.pointOfServices && response.pointOfServices.length > 0) {
                setTimeout(() => {
                  this.adjustZoomBasedOnRadius({
                    latitude: response.pointOfServices![0].geoPoint?.latitude ?? 0,
                    longitude: response.pointOfServices![0].geoPoint?.longitude ?? 0,
                  });
                }, 500);
              }
            } else {
              setTimeout(() => this.fitBounds(), 500);
            }
          }
        }
      }),
    );
  }

  adjustZoomBasedOnRadius(position: GeoPoint): void {
    if (this.mapElement?.googleMap) {
      const bounds = new google.maps.LatLngBounds();
      const center = new google.maps.LatLng(position.latitude ?? 0, position.longitude ?? 0);
      const circle = new google.maps.Circle({
        center,
        radius: this.getCircleRadius(),
      });

      bounds.union(circle.getBounds()!);
      this.mapElement.googleMap.fitBounds(bounds);
    }
  }

  getCircleRadius(): number {
    const unit = this.pointOfServicesRequestConfig$.value.unit;
    const distance = parseFloat(this.pointOfServicesRequestConfig$.value.distance || '0');
    const unitConversion = unit === 'MILES' ? 1609.344 : 1000;
    return distance * unitConversion;
  }

  selectAddress(name: string, marker?: MapMarker, template?: HTMLDivElement): void {
    this.selectedPosCode = name;
    const POS = (this.pointOfServicesRequest$.value.pointOfServices || []).find(pos => pos.name === name);

    if (POS) {
      this.selectedPOS = POS;

      if ((POS.geoPoint?.latitude, POS.geoPoint?.longitude)) {
        this.focusOnMarker(POS.geoPoint.latitude!, POS.geoPoint?.longitude);
      }

      this.scrollToItem(name);
      this.cdr.detectChanges();
    }

    if (this.infoWindow && marker && template) {
      this.infoWindow.options = {
        content: template?.innerHTML || '',
      };

      this.infoWindow.open(marker);
    }
  }

  focusOnMarker(lat: number, lng: number): void {
    this.center = { lat, lng };
  }

  scrollToItem(code: string): void {
    const $li = document.querySelector(`[data-code="${code}"]`) as HTMLLinkElement;

    if ($li && this.listContainer) {
      this.listContainer.nativeElement.scrollTo({
        top: $li.offsetTop - this.listContainer.nativeElement.offsetTop,
        behavior: 'smooth',
      });
    }
  }

  getCenter(): { lat: number; lng: number } {
    const POSs = this.pointOfServicesRequest$.value.pointOfServices || [];
    const totalAddresses = POSs.length;
    const sumLat = POSs.reduce((sum, addr) => sum + addr.geoPoint!.latitude!, 0);
    const sumLng = POSs.reduce((sum, addr) => sum + addr.geoPoint!.longitude!, 0);

    return {
      lat: sumLat / totalAddresses,
      lng: sumLng / totalAddresses,
    };
  }

  fitBounds(): void {
    const bounds = new google.maps.LatLngBounds();
    const POSs = this.pointOfServicesRequest$.value.pointOfServices || [];

    if (POSs.length) {
      POSs.forEach(pos => {
        bounds.extend(new google.maps.LatLng(pos.geoPoint!.latitude!, pos.geoPoint?.longitude));
      });

      this.mapElement.fitBounds(bounds);

      const currentZoom = this.mapElement?.getZoom() || 0;
      const maxZoom = 15;

      if (currentZoom > maxZoom) {
        this.mapElement.googleMap?.setZoom(maxZoom);
      }
    }
  }

  isOutsideRadius(pos: PointOfService): boolean {
    const numericPosDistance = pos.distanceKm || 0;
    let requestDistance = parseFloat(
      this.pointOfServicesRequestConfig$.value.distance || '0'
    );
    if (this.pointOfServicesRequestConfig$.value.unit === 'MILES'){
      requestDistance=requestDistance*1.60934
    }

    return numericPosDistance > requestDistance && pos.type == 'SALES_REP';
  }

}
